import React from 'react';
import type { Theme } from '@material-ui/core';
import { Divider, Drawer, List } from '@material-ui/core';
import type { SidebarMenuItem } from '../classes/SidebarMenuItem';
import { SidebarMenuDynamicItems } from './SidebarMenuDynamicItems';
import { SidebarTitle } from './SidebarTitle';
import { NestedSidebarMenuItem } from './NestedSidebarMenuItem';
import { IconName } from '@klp/design/Illustrations';

import {
    environmentMenuItems,
    keyFiguresMenuItems,
    leverandorMenuItems,
    projectMenuItems,
} from '@forvalter/components/MenuItems';
import { SidebarLanguages } from './SidebarLanguages';
import { SidebarUserInfo } from './SidebarUserInfo';
import classNames from 'classnames';
import { useLayoutContext } from '@klp/layout/Layout/LayoutContext';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { SidebarUserAdministration } from './SidebarUserAdministration';
import { SelectedContextSidebar } from '@klp/contextSelector/components/SelectedContextSidebar';

const styles = (theme: Theme) => ({
    drawer: {
        // Default background color for the drawer
        backgroundColor: theme.palette.secondary.dark,
        zIndex: theme.zIndex.drawer - 2,
        '& > div': {
            width: `calc(var(--sidebar-closed-width ))`,
            transition: `${theme.layout.transition} !important`,
            backgroundColor: 'transparent',
            overflowX: 'hidden',
        },
        '& .Sidebar__Menu': {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
        },
        // Dividers
        '& hr': {
            backgroundColor: theme.palette.primary.mediumLight,
            opacity: 0.4,
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },

        // List elements
        '& nav': {
            '& .MuiListItem-root': {
                height: 51,
                overflow: 'hidden',
            },
            '& .MuiListItemIcon-root': {
                minWidth: 40,
                justifyContent: 'center',
            },
            '& .MuiListItemText-root': {
                whiteSpace: 'nowrap',
                paddingLeft: '0.5em',
                opacity: 0,
                transition: 'opacity 0.25s linear',
            },
            '& > [role=button]': {
                '&:hover': {
                    backgroundColor: theme.palette.secondary.veryLight,
                    color: 'black',
                    '& svg': {
                        fill: theme.palette.primary.main,
                    },
                },
                '&.Mui-selected': {
                    color: theme.palette.background.paper,
                    backgroundColor: theme.palette.secondary.main,
                    '& svg': {
                        fill: theme.palette.background.paper,
                    },
                    '&:hover': {
                        color: theme.palette.background.paper,
                        backgroundColor: theme.palette.secondary.main,
                        '& svg': {
                            fill: theme.palette.background.paper,
                        },
                    },
                },
            },
            '& .Sidebar__MenuList--nested': {
                transition: theme.layout.transition,
                paddingLeft: theme.spacing(4),
            },
        },
        '& .Sidebar__StandaloneButton': {
            color: theme.palette.background.paper,
            borderColor: theme.palette.primary.light,
            '&:hover': {
                backgroundColor: theme.palette.secondary.main,
            },
        },
        // Sidebar open and closed states
        '.IsDesktop.MenuOpen &': {
            '& > div': {
                width: `calc(var(--sidebar-open-width ))`,
            },
            '& nav': {
                '& .MuiListItemText-root': {
                    opacity: 1,
                },
            },
        },
        '.IsDesktop.MenuClosed &': {
            '& nav': {
                '& .Sidebar__MenuList--nested': {
                    paddingLeft: theme.spacing(0),
                },
            },
        },
    },

    drawerMobile: {
        '& > div': {
            width: '100%',
        },
        '& nav': {
            '& .MuiListItemText-root': {
                opacity: 1,
            },
        },
    },
    list: {
        paddingTop: '1px',
        color: theme.palette.primary.veryLight,
    },
});
export type SidebarDrawerProps = {
    menuItems: Array<SidebarMenuItem>;
};

export const SidebarDrawer = ({ menuItems }: SidebarDrawerProps) => {
    const { menuOpen, isMobile, toggleMenu } = useLayoutContext();

    const classes = useKlpStyles({}, styles);

    return (
        <Drawer
            className={classNames('Sidebar', classes.drawer, {
                [classes.drawerMobile]: isMobile,
            })}
            variant={isMobile ? 'temporary' : 'persistent'}
            anchor="left"
            open={isMobile ? menuOpen : true}
            onMouseEnter={!isMobile ? () => toggleMenu(true) : undefined}
            onMouseLeave={!isMobile ? () => toggleMenu(false) : undefined}
            onClose={(event, reason) => {
                if (reason === 'backdropClick') {
                    toggleMenu(false);
                }
            }}
            PaperProps={{
                style: {
                    position: 'sticky',
                    zIndex: 2000,
                    height: isMobile ? '100%' : 'auto',
                },
            }}
            data-testid="Sidebar"
        >
            <div className="Sidebar__Menu" data-testid="Sidebar__Menu">
                <SidebarTitle />
                <List component="nav" className={classes.list}>
                    <SidebarMenuDynamicItems menuItems={menuItems} />
                    <NestedSidebarMenuItem
                        icon={IconName.PLANT}
                        translation="environment.title"
                        nestedItems={environmentMenuItems}
                    />
                    <NestedSidebarMenuItem
                        icon={IconName.HORIZONTAL_BAR_CHART}
                        translation="keyfigures.title"
                        nestedItems={keyFiguresMenuItems}
                    />
                    <NestedSidebarMenuItem
                        icon={IconName.DOC_INBOUND}
                        translation="project.menuItems.root"
                        nestedItems={projectMenuItems}
                    />
                    <NestedSidebarMenuItem
                        icon={IconName.DOC_INTERNAL}
                        translation="leverandor.menuItems.root"
                        nestedItems={leverandorMenuItems}
                    />
                    <Divider />
                    <SidebarLanguages />

                    <SelectedContextSidebar />

                    <SidebarUserAdministration />

                    <SidebarUserInfo />
                </List>
            </div>
        </Drawer>
    );
};
